import { Type } from '@scalar/typebox';
import { boolean, object, optional, record, string } from '@scalar/validation';
/**
 * Schema for parameter disabled states within a single example.
 * Maps parameter names (strings) to their disabled state (boolean).
 */
const ExampleParameterStateSchema = Type.Record(Type.String(), Type.Boolean());
/**
 * Schema for all example parameter states across multiple examples.
 * Maps example keys (strings) to their parameter disabled states.
 */
const ExamplesParameterStatesSchema = Type.Record(Type.String(), ExampleParameterStateSchema);
/**
 * Custom OpenAPI extension to track which parameters are disabled across different contexts.
 *
 * This extension allows the API client to persist disabled states for different types of
 * parameters (global cookies, global headers, default headers) across multiple examples.
 *
 * This is necessary because:
 * - Different parameter types have different scopes and behaviors
 * - Users need to disable specific parameters per example without affecting others
 * - The disabled state must persist across sessions
 * - Global parameters can be disabled independently of operation-specific ones
 *
 * Structure:
 * - Top level: Parameter category ("global-cookies", "global-headers", "default-headers")
 * - Second level: Example keys (like "default", "custom-example")
 * - Third level: Parameter names (like "Content-Type", "Cookie")
 * - Values: true = disabled, false = enabled
 *
 * @example
 * ```json
 * {
 *   "x-scalar-disable-parameters": {
 *     "global-cookies": {
 *       "default": {
 *         "session": true
 *       }
 *     },
 *     "global-headers": {
 *       "default": {
 *         "X-API-Key": false
 *       }
 *     },
 *     "default-headers": {
 *       "default": {
 *         "Content-Type": true,
 *         "Accept": false
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const XScalarDisableParametersSchema = Type.Object({
    'x-scalar-disable-parameters': Type.Optional(Type.Object({
        'global-cookies': Type.Optional(ExamplesParameterStatesSchema),
        'global-headers': Type.Optional(ExamplesParameterStatesSchema),
        'default-headers': Type.Optional(ExamplesParameterStatesSchema),
    })),
});
const ExampleParameterState = record(string(), boolean());
const ExamplesParameterStates = record(string(), ExampleParameterState);
const DisableParametersInner = object({
    'global-cookies': optional(ExamplesParameterStates),
    'global-headers': optional(ExamplesParameterStates),
    'default-headers': optional(ExamplesParameterStates),
}, {
    typeName: 'DisableParametersConfig',
    typeComment: 'Disabled parameter state by category and example',
});
export const XScalarDisableParameters = object({
    'x-scalar-disable-parameters': optional(DisableParametersInner),
}, {
    typeName: 'XScalarDisableParameters',
    typeComment: 'Tracks which parameters are disabled across examples',
});
